/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import Card from "components/card";
import axios from 'axios';
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";
import { SubaccountContext } from "../../../../store/SubaccountProvider";

interface Props {
    isOpen: boolean;
    onClose(): void;
    tradeDetail: any;
    offerDetail: any;
    setSwalProps?: any;
    setOfferInfo?: any;
}

interface PayloadType {
    [key: string]: any;
}

const AcceptOfferConfirmationModal: React.FC<Props> = ({
    isOpen,
    onClose,
    tradeDetail,
    offerDetail,
    setSwalProps,
    setOfferInfo,
}) => {
    const {
        subaccountBalance,
        setSubaccountBalance,
        isGettingBalance,
        setIsGettingBalance,
    } = useContext(SubaccountContext);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
    const subaccount_id = localStorage.getItem("subaccountId");

    const handleSubmit = async () => {
        if (!isConfirmed) return;

        setIsProcessing(true);
        try {
            await axios
                .post(process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole").toLowerCase()}/trade/action/trade-update-offer-status`, {
                    offer_id: offerDetail?.offer_id,
                    trade_id: tradeDetail.id,
                    trade_shortid: tradeDetail.shortid,
                    ticket_id: tradeDetail.ticket_id,
                    data: {
                        offerId: offerDetail?.offer_id
                    },
                    status: "accepted"
                })
                .then(function (response) {
                    if (response && response.data?.status === "ok") {
                        if (setOfferInfo) {
                            setOfferInfo(response.data);
                        }

                        closeModal();

                        if (setSwalProps) {
                            setSwalProps({
                                show: true,
                                icon: 'success',
                                title: 'Success',
                                html: `Accept has been submitted successfully`,
                                showConfirmButton: true,
                                didClose: async () => {
                                    setIsGettingBalance(true);
                                    await getSubaccountBalances();
                                    setIsGettingBalance(false);
                                    setSwalProps({});
                                },
                            });
                        }
                    } else {
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: response.data?.message || 'An error occurred while accepting your offer.',
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    }
                })
                .catch(function (error) {
                    let errorMessage = "Offer submission has failed";
                    if (
                        error.response &&
                        (error.response.status === 400 || error.response.status === 500)
                    ) {
                        errorMessage = error.response.data.message;
                    }

                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: errorMessage,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                },
            });
        }
    };

    const getSubaccountBalances = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL +
                `/api/subaccount/get-subaccount-balance`
            );

            const balances = response.data?.balances;
            if (balances) {
                setSubaccountBalance(balances);
            }
        } catch (error) {
            console.error("Error get balance data", error);
            return false;
        }
    };

    const closeModal = async () => {
        setIsProcessing(false);
        setIsConfirmed(false);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeModal}
            closeOnOverlayClick={false}
        >
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto w-full max-w-[500px] px-4 md:top-[12vh]">
                <ModalBody className="p-0 !text-navy-700">
                    <Card extra="px-6 pt-8 pb-10 flex flex-col !z-[1004]">
                        <h1 className="mb-[20px] text-2xl font-bold">Accept Offer Confirmation</h1>
                        <div className="mb-[20px] text-sm">
                            <div className="mb-4">
                                You're about to accept this offer. This action will confirm all associated trade details. Please confirm if you wish to proceed.
                            </div>
                            <div className="mb-1">
                                <span>Sell:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.sell)}
                                        currency={offerDetail.sell_asset}
                                        decimal={tradeDetail?.from_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.sell_asset}
                                </span>
                            </div>
                            <div className="mb-1">
                                <span>Receive:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.receive)}
                                        currency={offerDetail.receive_asset}
                                        decimal={tradeDetail?.to_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.receive_asset}
                                </span>
                            </div>
                            <div className="mb-1">
                                <span>Rate:</span>
                                <span className="ml-3 font-semibold">
                                    <DisplayAmountWithDecimals
                                        amount={Number(offerDetail.rate)}
                                        currency={offerDetail.sell_asset}
                                        decimal={tradeDetail?.from_asset_decimal}
                                    />
                                    &nbsp;{offerDetail.sell_asset}
                                </span>
                            </div>
                            {tradeDetail.from_network === "fiat" &&
                                <div className="mb-1">
                                    <span>Settlement Address:</span>
                                    <span className="ml-3 font-semibold">
                                        {tradeDetail?.to_address?.address.slice(0, 15)}...{tradeDetail?.to_address?.address.slice(-6)}
                                    </span>
                                </div>
                            }
                        </div>

                        <div className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-white/10 p-4 mb-4">
                            <input
                                type="checkbox"
                                id="confirmAccept"
                                checked={isConfirmed}
                                onChange={(e) => setIsConfirmed(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label htmlFor="confirmAccept" className="text-sm text-navy-700 dark:text-white cursor-pointer">
                                I have carefully reviewed all the offer details above and confirm that I want to accept this offer. I understand that this action cannot be undone once accepted.
                            </label>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <Button
                                onClick={() => closeModal()}
                                isDisabled={isProcessing}
                                className="btn btn-default"
                            >
                                Not now
                            </Button>
                            <Button
                                onClick={() => handleSubmit()}
                                isLoading={isProcessing}
                                loadingText='Processing...'
                                isDisabled={!isConfirmed || isProcessing}
                                className="btn text-white bg-red-600 hover:bg-red-500">
                                Accept offer
                            </Button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AcceptOfferConfirmationModal;